import { AppDispatch, RootState } from '@/store'
import { addFile } from '@/store/slices/filesSlice'
import {
  UploadItem,
  uploadConflicted,
  uploadFailed,
  uploadHashed,
  uploadProgress,
  uploadSkipped,
  uploadStarted,
  uploadSucceeded,
  uploadsQueued,
  folderNavigationRequested,
} from '@/store/slices/uploadsSlice'
import { uploadFileRegistry } from '@/services/uploadFileRegistry'
import apiService from '@/services/api'
import { canHashLocally, sha256File } from '@/utils/checksum'

/**
 * Hashing reads a whole file into memory (crypto.subtle has no streaming API)
 * and uploads compete for bandwidth, so only a few files are in flight at once.
 */
const MAX_CONCURRENT_UPLOADS = 3

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  let cursor = 0

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++
      await worker(items[index])
    }
  })

  await Promise.all(runners)
}

function errorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { message?: string; error?: string } } })?.response
  return response?.data?.message || response?.data?.error || (error as Error)?.message || 'Upload failed'
}

/** A 409 means the server found a duplicate the pre-flight missed (or skipped). */
function conflictFromError(error: unknown) {
  const response = (error as { response?: { status?: number; data?: any } })?.response

  if (response?.status !== 409) {
    return null
  }

  return {
    inTargetFolder: response.data?.existing ?? null,
    elsewhere: [],
  }
}

/**
 * Send one already-queued item to the server, reporting progress as it goes.
 */
export const performUpload =
  (id: string, options: { allowDuplicate?: boolean } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const item = getState().uploads.items.find((candidate) => candidate.id === id)
    const file = uploadFileRegistry.get(id)

    if (!item || !file) {
      return
    }

    dispatch(uploadStarted({ id }))

    try {
      const uploaded = await apiService.uploadFileWithProgress(
        file,
        item.folderId ?? undefined,
        (progress) => dispatch(uploadProgress({ id, progress })),
        { allowDuplicate: options.allowDuplicate }
      )

      dispatch(addFile({ file: uploaded, folderId: item.folderId }))
      dispatch(uploadSucceeded({ id, fileId: uploaded.id }))
      uploadFileRegistry.remove(id)
    } catch (error) {
      const conflict = conflictFromError(error)

      if (conflict) {
        dispatch(uploadConflicted({ id, conflict }))
        return
      }

      dispatch(uploadFailed({ id, error: errorMessage(error) }))
      uploadFileRegistry.remove(id)
    }
  }

/**
 * Queue files, hash each one, ask the server whether it already exists, and
 * either upload it or stop and wait for the user to decide.
 */
export const startFileUploads =
  (params: { files: File[]; folderId?: number | null; folderName?: string | null }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const { files, folderId = null, folderName = null } = params

    if (files.length === 0) {
      return
    }

    const items: UploadItem[] = files.map((file) => {
      const id = crypto.randomUUID()
      uploadFileRegistry.set(id, file)

      return {
        id,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        folderId,
        folderName,
        status: 'hashing',
        progress: 0,
      }
    })

    dispatch(uploadsQueued(items))

    await runWithConcurrency(items, MAX_CONCURRENT_UPLOADS, async (item) => {
      const file = uploadFileRegistry.get(item.id)

      if (!file) {
        return
      }

      // Without a secure context we cannot hash, so skip the pre-flight and let
      // the server's 409 surface the conflict instead.
      if (!canHashLocally()) {
        await dispatch(performUpload(item.id))
        return
      }

      try {
        const checksum = await sha256File(file)
        dispatch(uploadHashed({ id: item.id, checksum }))

        const result = await apiService.checkFileDuplicate(checksum, item.folderId)

        if (result.duplicate) {
          dispatch(
            uploadConflicted({
              id: item.id,
              conflict: {
                inTargetFolder: result.in_target_folder,
                elsewhere: result.elsewhere ?? [],
              },
            })
          )
          return
        }
      } catch (error) {
        // The check is advisory — a failure here must not block the upload.
        console.warn('Duplicate pre-flight check failed, uploading anyway:', error)
      }

      await dispatch(performUpload(item.id))
    })
  }

/** Apply the user's answer to a duplicate prompt. */
export const resolveUploadConflict =
  (params: { id: string; action: 'upload' | 'skip' }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    if (params.action === 'skip') {
      dispatch(uploadSkipped({ id: params.id }))
      uploadFileRegistry.remove(params.id)
      return
    }

    await dispatch(performUpload(params.id, { allowDuplicate: true }))
  }

/**
 * Folder uploads go up as one request, so they show as a single aggregate row.
 */
export const startFolderUpload =
  (params: { files: FileList; parentFolderId?: number | null; folderName?: string | null }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const { files, parentFolderId = null } = params

    if (files.length === 0) {
      return
    }

    const first = files[0] as File & { webkitRelativePath?: string }
    const folderName =
      params.folderName ?? (first?.webkitRelativePath?.split('/')[0] || 'folder')

    const id = crypto.randomUUID()

    dispatch(
      uploadsQueued([
        {
          id,
          name: folderName,
          size: Array.from(files).reduce((total, file) => total + file.size, 0),
          mimeType: 'inode/directory',
          folderId: parentFolderId,
          folderName: null,
          status: 'uploading',
          progress: 0,
          fileCount: files.length,
        },
      ])
    )

    try {
      const result = await apiService.uploadFolder(files, parentFolderId ?? undefined)

      result.uploaded?.forEach((entry) => {
        dispatch(addFile({ file: entry.file, folderId: entry.target_folder_id }))
      })

      dispatch(uploadSucceeded({ id }))

      dispatch(
        folderNavigationRequested({
          rootFolderId: result.root_folder?.id ?? null,
          foldersCreated: result.folders_created ?? 0,
          rootFolderName: folderName,
        })
      )
    } catch (error) {
      dispatch(uploadFailed({ id, error: errorMessage(error) }))
    }
  }
